/**
 * Hub kit barrel. Explicit re-exports — no runtime crawling.
 *
 * Pair these components with the schemas in
 * `packages/generative-ui/src/hubs/` (see info-hubs/technical-spec.md). The
 * agent emits `HubSection[]`; the page renderer resolves product/report IDs
 * against `HubRenderContext` and passes both to a kit component.
 */

import type { ReactNode } from 'react'
import type { HubSection, HubRenderContext } from './types'

import { HubAnchorLinkList } from './HubAnchorLinkList'
import { HubHero } from './HubHero'
import { HubIntro } from './HubIntro'
import { HubTopPicks } from './HubTopPicks'
import { HubProductRail } from './HubProductRail'
import { HubReportGrid } from './HubReportGrid'
import { HubGuideSteps } from './HubGuideSteps'
import { HubGuidePathways } from './HubGuidePathways'
import { HubDecisionBlock } from './HubDecisionBlock'
import { HubConcernLinks } from './HubConcernLinks'
import { HubComparisonTable } from './HubComparisonTable'
import { HubFaq } from './HubFaq'
import { HubSourcesSummary } from './HubSourcesSummary'
import { HubCtaBand } from './HubCtaBand'
import { HubPullQuote } from './HubPullQuote'
import { HubStatCallout } from './HubStatCallout'
import { HubMethodologyCard } from './HubMethodologyCard'
import { HubSiblingBand } from './HubSiblingBand'

export {
  HubAnchorLinkList,
  HubHero,
  HubIntro,
  HubTopPicks,
  HubProductRail,
  HubReportGrid,
  HubGuideSteps,
  HubGuidePathways,
  HubDecisionBlock,
  HubConcernLinks,
  HubComparisonTable,
  HubFaq,
  HubSourcesSummary,
  HubCtaBand,
  HubPullQuote,
  HubStatCallout,
  HubMethodologyCard,
  HubSiblingBand,
}

export type {
  HubSection,
  HubRenderContext,
  HubSectionComponentProps,
} from './types'

export * from './types'

/**
 * Render dispatch. Switching here (rather than a Record map) keeps each branch
 * narrowed against the discriminated `HubSection` union, so each component
 * receives its exact section type without casts.
 */
export function renderHubSection(
  section: HubSection,
  context: HubRenderContext
): ReactNode {
  switch (section.type) {
    case 'hero':
      return <HubHero section={section} context={context} />
    case 'intro':
      return <HubIntro section={section} context={context} />
    case 'anchorLinkList':
      return <HubAnchorLinkList section={section} context={context} />
    case 'topPicks':
      return <HubTopPicks section={section} context={context} />
    case 'productRail':
      return <HubProductRail section={section} context={context} />
    case 'reportGrid':
      return <HubReportGrid section={section} context={context} />
    case 'guideSteps':
      return <HubGuideSteps section={section} context={context} />
    case 'guidePathways':
      return <HubGuidePathways section={section} context={context} />
    case 'decisionBlock':
      return <HubDecisionBlock section={section} context={context} />
    case 'concernLinks':
      return <HubConcernLinks section={section} context={context} />
    case 'comparisonTable':
      return <HubComparisonTable section={section} context={context} />
    case 'faq':
      return <HubFaq section={section} context={context} />
    case 'sourcesSummary':
      return <HubSourcesSummary section={section} context={context} />
    case 'ctaBand':
      return <HubCtaBand section={section} context={context} />
    case 'pullQuote':
      return <HubPullQuote section={section} context={context} />
    case 'statCallout':
      return <HubStatCallout section={section} context={context} />
    case 'methodologyCard':
      return <HubMethodologyCard section={section} context={context} />
    default: {
      const _exhaustive: never = section
      return _exhaustive
    }
  }
}

export function shouldRenderHubSection(
  section: HubSection,
  context: HubRenderContext
): boolean {
  switch (section.type) {
    case 'hero':
    case 'decisionBlock':
    case 'ctaBand':
      return true
    case 'intro':
      return section.body
        .split(/\n\n+/)
        .map((paragraph) => paragraph.trim())
        .some(Boolean)
    case 'anchorLinkList':
      return (
        section.links.length > 0 || Object.keys(context.reportsById).length > 0
      )
    case 'topPicks':
      return section.picks.some((pick) => context.productsById[pick.productId])
    case 'productRail':
      return section.productIds.some((id) => context.productsById[id])
    case 'reportGrid':
      return section.items.some((item) => context.reportsById[item.reportId])
    case 'guideSteps':
      return section.steps.length > 0
    case 'guidePathways':
      return section.pathways.some((item) => context.reportsById[item.reportId])
    case 'concernLinks':
      return section.links.length > 0
    case 'comparisonTable':
      return section.columns.length > 0 && section.rows.length > 0
    case 'faq':
      return section.items.length > 0
    case 'sourcesSummary':
      return section.totalSources > 0
    case 'pullQuote':
      return Boolean(section.quote && section.quote.trim().length > 0)
    case 'statCallout':
      return section.stats.length > 0
    case 'methodologyCard':
      return Boolean(section.body && section.body.trim().length > 0)
    default: {
      const _exhaustive: never = section
      return _exhaustive
    }
  }
}
